// File: src/components/inventory/AssignItemModal.tsx

import React, { useState, useEffect } from 'react';
import { InventoryItem as APIInventoryItem } from '../../services/inventoryAPI';
import { User } from '../../types/user';
import { AssignmentFormData, ASSIGNMENT_REASONS } from '../../types/assignment';
import { userAPI } from '../../services/userAPI';

interface AssignItemModalProps {
  isOpen: boolean;
  item: APIInventoryItem | null;
  onClose: () => void;
  onAssign: (assignmentData: AssignmentFormData) => Promise<void>;
}

const FIREARM_CONFIRMATION_METHODS = [
  'In-Person Training',
  'Digital Acknowledgment',
  'Signed Document',
  'Verbal Confirmation'
];

export const AssignItemModal: React.FC<AssignItemModalProps> = ({
  isOpen,
  item,
  onClose,
  onAssign
}) => {
  const [officers, setOfficers] = useState<User[]>([]);
  const [selectedOfficerId, setSelectedOfficerId] = useState('');
  const [selectedOfficerName, setSelectedOfficerName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState(ASSIGNMENT_REASONS.INITIAL_ISSUE);
  const [assignmentDate, setAssignmentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [error, setError] = useState('');
  
  // Firearm policy fields
  const [firearmPolicyConfirmed, setFirearmPolicyConfirmed] = useState(false);
  const [confirmedBy, setConfirmedBy] = useState('');
  const [confirmationMethod, setConfirmationMethod] = useState('In-Person Training');

  // Get current user name for "assigned by"
  const [currentUserName, setCurrentUserName] = useState('');

  const isFirearm = item?.category?.toLowerCase().includes('firearm') || 
                   item?.category?.toLowerCase().includes('weapon') ||
                   item?.name?.toLowerCase().includes('pistol') ||
                   item?.name?.toLowerCase().includes('rifle') ||
                   item?.name?.toLowerCase().includes('gun');

  const isBulkItem = item?.itemType !== 'SERIALIZED';
  const maxQuantity = isBulkItem ? (item as any)?.availableQuantity || 0 : 1;

  useEffect(() => {
    if (isOpen) {
      loadOfficers();
      loadCurrentUser();
      resetForm();
    }
  }, [isOpen]);

  const loadOfficers = async () => {
    try {
      setLoadingOfficers(true);
      const response = await userAPI.getUsers();
      
      // Handle different response formats
      let usersArray: any[] = [];
      if (Array.isArray(response)) {
        usersArray = response;
      } else if (response && typeof response === 'object') {
        // Handle APIResponse wrapper or other object structures
        usersArray = (response as any).data || (response as any).users || [];
      }
      
      // Filter for active officers only
      const activeOfficers = usersArray.filter((user: any) => 
        user.employmentStatus === 'Active' && 
        user.rank && 
        user.rank !== 'System Administrator'
      );
      setOfficers(activeOfficers);
    } catch (err) {
      console.error('Error loading officers:', err);
      setError('Failed to load officers');
    } finally {
      setLoadingOfficers(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      // TODO: Get from auth context
      setCurrentUserName('Current User');
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  };

  const resetForm = () => {
    setSelectedOfficerId('');
    setSelectedOfficerName('');
    setQuantity(1);
    setReason(ASSIGNMENT_REASONS.INITIAL_ISSUE);
    setAssignmentDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setFirearmPolicyConfirmed(false);
    setConfirmedBy('');
    setConfirmationMethod('In-Person Training');
    setError('');
  };

  const handleOfficerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const officerId = e.target.value;
    setSelectedOfficerId(officerId);
    
    const officer = officers.find(o => o.userId === officerId);
    if (officer) {
      setSelectedOfficerName(`${officer.firstName} ${officer.lastName}`);
    }
  };

  const validateForm = (): boolean => {
    if (!selectedOfficerId) {
      setError('Please select an officer');
      return false;
    }

    if (isBulkItem && (quantity < 1 || quantity > maxQuantity)) {
      setError(`Quantity must be between 1 and ${maxQuantity}`);
      return false;
    }

    if (isFirearm && !firearmPolicyConfirmed) {
      setError('Firearm policy confirmation is required');
      return false;
    }

    if (isFirearm && firearmPolicyConfirmed && !confirmedBy.trim()) {
      setError('Please enter who confirmed the firearm policy');
      return false;
    }

    setError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const assignmentData: AssignmentFormData = {
        inventoryItemId: item!.itemId,
        assignedToUserId: selectedOfficerId,
        assignmentReason: reason,
        notes: isFirearm && firearmPolicyConfirmed 
          ? `${notes}\nFirearm Policy Confirmed by: ${confirmedBy} (${confirmationMethod})`.trim()
          : notes,
        confirmFirearmPolicy: isFirearm ? firearmPolicyConfirmed : undefined
      };

      await onAssign(assignmentData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign item');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !item) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Assign Equipment</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Item Information */}
          <div className="bg-gray-50 p-3 rounded">
            <div className="font-medium">{item.name}</div>
            {item.itemType === 'SERIALIZED' && (item as any).serialNumber && (
              <div className="text-sm text-gray-600">Serial: {(item as any).serialNumber}</div>
            )}
            {isFirearm && (
              <div className="text-sm font-medium text-red-600 mt-1">⚠️ Firearm - Policy Review Required</div>
            )}
          </div>

          {/* Officer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign to Officer *
            </label>
            {loadingOfficers ? (
              <div className="text-sm text-gray-500">Loading officers...</div>
            ) : (
              <select
                value={selectedOfficerId}
                onChange={handleOfficerChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loading}
              >
                <option value="">Select an officer...</option>
                {officers.map(officer => (
                  <option key={officer.userId} value={officer.userId}>
                    {officer.badgeNumber} - {officer.firstName} {officer.lastName} ({officer.rank})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Quantity (for bulk items only) */}
          {isBulkItem && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity *
              </label>
              <input
                type="number"
                min="1"
                max={maxQuantity}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={loading}
              />
              <div className="text-xs text-gray-500 mt-1">
                Max available: {maxQuantity}
              </div>
            </div>
          )}

          {/* Assigned By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned by
            </label>
            <input
              type="text"
              value={currentUserName}
              className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
              readOnly
            />
          </div>

          {/* Assignment Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as any)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={loading}
            >
              {Object.values(ASSIGNMENT_REASONS).map(reasonOption => (
                <option key={reasonOption} value={reasonOption}>
                  {reasonOption}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignment Date *
            </label>
            <input
              type="date"
              value={assignmentDate}
              onChange={(e) => setAssignmentDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={loading}
            />
          </div>

          {/* Firearm Policy Confirmation */}
          {isFirearm && (
            <div className="border border-red-200 bg-red-50 p-3 rounded">
              <div className="flex items-start space-x-2 mb-3">
                <input
                  type="checkbox"
                  id="firearmPolicy"
                  checked={firearmPolicyConfirmed}
                  onChange={(e) => setFirearmPolicyConfirmed(e.target.checked)}
                  className="mt-1"
                  disabled={loading}
                />
                <label htmlFor="firearmPolicy" className="text-sm font-medium text-red-800">
                  Firearm Policy Review Confirmed *
                </label>
              </div>

              {firearmPolicyConfirmed && (
                <>
                  <div className="mb-2">
                    <label className="block text-sm font-medium text-red-700 mb-1">
                      Confirmed by *
                    </label>
                    <input
                      type="text"
                      value={confirmedBy}
                      onChange={(e) => setConfirmedBy(e.target.value)}
                      placeholder="Enter name of confirming person"
                      className="w-full p-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      required
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-1">
                      Confirmation Method *
                    </label>
                    <select
                      value={confirmationMethod}
                      onChange={(e) => setConfirmationMethod(e.target.value)}
                      className="w-full p-2 border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500"
                      required
                      disabled={loading}
                    >
                      {FIREARM_CONFIRMATION_METHODS.map(method => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes about this assignment..."
              disabled={loading}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Assigning...' : 'Assign Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};